// Phase 2 §4: Project to Cap Table Button
// Triggers edge function to create projection round from scenario

import { useState } from 'react';
import { BarChart3, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ProtectedAction } from '@/components/auth/ProtectedAction';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useProjectionMode } from '@/hooks/useProjectionMode';

interface ProjectToCapTableButtonProps {
  companyId: string;
  scenarioId?: string;
  scenarioName: string;
  scenarioData: any;
  disabled?: boolean;
}

export function ProjectToCapTableButton({
  companyId,
  scenarioId,
  scenarioName,
  scenarioData,
  disabled = false,
}: ProjectToCapTableButtonProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [projecting, setProjecting] = useState(false);
  const { setMode } = useProjectionMode();

  async function handleProject() {
    try {
      setProjecting(true);

      // Call the edge function to create projection round
      const { data, error } = await supabase.functions.invoke('project-scenario-to-cap-table', {
        body: {
          company_id: companyId,
          scenario_id: scenarioId || crypto.randomUUID(),
          round_label: `Projection - ${scenarioName}`,
          pre_money: scenarioData?.pre_money || 0,
          raise_amount: scenarioData?.raise || 0,
          option_pool_refresh_pct: scenarioData?.option_pool_increase || 0,
        },
      });

      if (error) throw error;

      console.log('Projection created:', data);

      toast.success('Projection created successfully', {
        description: 'Switch to Scenario Mode to view the projection',
      });

      // Auto-switch to Scenario Mode
      setMode('SCENARIO');
      setDialogOpen(false);
    } catch (error: any) {
      console.error('Error projecting scenario:', error);
      toast.error('Failed to project scenario', {
        description: error.message || 'Please try again',
      });
    } finally {
      setProjecting(false);
    }
  }

  return (
    <>
      <ProtectedAction
        allowedRoles={['owner', 'admin']}
        companyId={companyId}
        fallback={
          <Button variant="outline" size="sm" disabled>
            <BarChart3 className="h-4 w-4 mr-2" />
            Project (Admin Only)
          </Button>
        }
      >
        <Button
          variant="outline"
          size="sm"
          onClick={() => setDialogOpen(true)}
          disabled={disabled || projecting}
        >
          {projecting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Projecting...
            </>
          ) : (
            <>
              <BarChart3 className="h-4 w-4 mr-2" />
              Project to Cap Table
            </>
          )}
        </Button>
      </ProtectedAction>

      <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Project Scenario to Cap Table?</AlertDialogTitle>
            <AlertDialogDescription>
              This will create a projection round based on "{scenarioName}" in your cap table. The
              projection will be clearly marked and won't affect your legal records.
              <br />
              <br />
              <strong>Note:</strong> This is a what-if model only. It does not modify any legal
              equity data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleProject} disabled={projecting}>
              {projecting ? 'Projecting...' : 'Create Projection'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
